/**
 * Rate Limiter Service
 * 
 * This service provides rate limiting functionality for Firestore operations
 * to prevent excessive reads and writes that could lead to high costs.
 */

// Configuration
const DEFAULT_LIMIT = 1000; // Default operations per minute
const WINDOW_SIZE = 60 * 1000; // 1 minute in milliseconds

interface RateLimiterOptions {
  limit?: number;
  windowSize?: number;
}

interface OperationCounter {
  count: number;
  resetTime: number;
}

class RateLimiterService {
  private counters: Map<string, OperationCounter> = new Map();
  private globalCounter: OperationCounter;
  private limit: number;
  private windowSize: number;
  private enabled: boolean = true;
  private emergencyMode: boolean = false;

  constructor(options: RateLimiterOptions = {}) {
    this.limit = options.limit || DEFAULT_LIMIT;
    this.windowSize = options.windowSize || WINDOW_SIZE;
    this.globalCounter = {
      count: 0,
      resetTime: Date.now() + this.windowSize
    };

    // Set up periodic cleanup
    setInterval(() => this.cleanup(), this.windowSize);
  }

  /**
   * Check if an operation should be allowed
   * @param operationType Type of operation (read, write, delete)
   * @param collectionName Name of the collection being accessed
   * @returns True if operation is allowed, false if rate limit exceeded
   */
  canPerformOperation(operationType: string, collectionName: string): boolean {
    if (!this.enabled) return true;

    const now = Date.now();
    const key = `${operationType}:${collectionName}`;

    // Reset global counter if window has passed
    if (now > this.globalCounter.resetTime) {
      this.globalCounter = {
        count: 0,
        resetTime: now + this.windowSize
      };
    }

    // Get or create counter for this operation type and collection
    let counter = this.counters.get(key);
    if (!counter || now > counter.resetTime) {
      counter = {
        count: 0,
        resetTime: now + this.windowSize
      };
      this.counters.set(key, counter);
    }

    // Check if limit exceeded
    if (this.globalCounter.count >= this.limit) {
      
      
      // If we're in emergency mode, throw an error to prevent the operation
      if (this.emergencyMode) {
        throw new Error(`Rate limit exceeded: ${this.globalCounter.count}/${this.limit} operations. Operation blocked to prevent excessive costs.`);
      }
      
      return false;
    }

    // Increment counters
    counter.count++;
    this.globalCounter.count++;

    // Log warning if approaching limit
    if (this.globalCounter.count > this.limit * 0.8) {
      
    }

    return true;
  }

  /**
   * Enable emergency mode - throws errors instead of just returning false
   * Use this to completely block operations when limits are exceeded
   */
  enableEmergencyMode(): void {
    this.emergencyMode = true;
    
  }

  /**
   * Disable emergency mode
   */
  disableEmergencyMode(): void {
    this.emergencyMode = false;
  }

  /**
   * Enable rate limiting
   */
  enable(): void {
    this.enabled = true;
  }

  /**
   * Disable rate limiting
   */
  disable(): void {
    this.enabled = false;
  }

  /**
   * Update rate limit configuration
   */
  updateConfig(options: RateLimiterOptions): void {
    if (options.limit) this.limit = options.limit;
    if (options.windowSize) this.windowSize = options.windowSize;
  }

  /**
   * Get current rate limit status
   */
  getStatus(): { enabled: boolean; emergencyMode: boolean; currentCount: number; limit: number; windowSizeMs: number } {
    return {
      enabled: this.enabled,
      emergencyMode: this.emergencyMode,
      currentCount: this.globalCounter.count,
      limit: this.limit,
      windowSizeMs: this.windowSize
    };
  }

  /**
   * Reset all counters
   */
  reset(): void {
    this.counters.clear();
    this.globalCounter = {
      count: 0,
      resetTime: Date.now() + this.windowSize
    };
  }

  /**
   * Clean up expired counters
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, counter] of this.counters.entries()) {
      if (now > counter.resetTime) {
        this.counters.delete(key);
      }
    }
  }
}

// Create and export singleton instance
export const rateLimiter = new RateLimiterService();
